// auth_js
import axios from "axios";
import * as queries from "../graphql_hasura/queries";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  updatePassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import router from "../router";



// Initialize Firebase
const firebase_config = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebase_config);
const auth = getAuth(app);

export const auth_module = {
  namespaced: true,

  state: () => ({
    chosen_customer_id: null,
    user: null,
    jwt_token: null,
    loading: false,
    login_error: "",
    authorized: false,
    user_ddb: null,
    hasura_params: {
      url: process.env.VUE_APP_HASURA_ENDPOINT,
      headers: {
        Authorization: "",
        "Content-Type": "application/json",
      },
    },
  }),

  mutations: {
    set_user(state, user) {
      state.user = user;
      state.authorized = !!user;
    },
    set_jwt_token(state, token) {
      state.jwt_token = token;
    },
    set_login_error(state, error) {
      state.login_error = error;
    },
    set_loading(state, loading) {
      state.loading = loading;
    },
    set_user_data(state, user_data) {
      state.user_ddb = user_data;
    },
    set_hasura_params(state, auth_token) {
      state.hasura_params = {
        url: process.env.VUE_APP_HASURA_ENDPOINT,
        headers: {
          Authorization: "Bearer " + auth_token,
          "Content-Type": "application/json",
        },
      };
    },
    set_auth(state, auth) {
      state.auth = auth;
    }
  },

  actions: {
    async setupCdnAccess({state}, selected_customer_id) {
      try {
        console.log(state.jwt_token)
        const response = await fetch(
          "https://cdn-signer.onecup-portal.app/setCdnCookies",
          {
            method: "POST",
            credentials: "include", // Important for cookies
            headers: {
              Authorization: `Bearer ${state.jwt_token} SelectCustomer ${selected_customer_id}`,
              "Content-Type": "application/json",
              Origin: window.location.origin,
            },
          }
        );
        await response.json();
        console.log(response)
      } catch (error) {
        console.error("Error setting up CDN access:", error);
      }
    },
    async login({ commit, dispatch }, { email, password }) {
      commit("set_loading", true);
      commit("set_login_error", "");

      try {
        const user_credential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = user_credential.user;
        // Get the Firebase JWT token
        const token = await user.getIdToken(true);
        const tokenResult = await user.getIdTokenResult(true);

        if (!tokenResult.claims.role || tokenResult.claims.role !== 'portal_admin') {
          throw new Error('Access denied: Admin privileges required');
        }
        commit("set_auth", auth);
        commit("set_user", user);
        commit("set_jwt_token", token);
        commit("set_hasura_params", token);

        // Set up token refresh
        onIdTokenChanged(auth, async (user) => {
          if (user) {
            const new_token = await user.getIdToken(true);
            commit("set_auth", auth);
            commit("set_jwt_token", new_token);
            commit("set_hasura_params", new_token);
          }
        });

        // Get additional user data if needed
        await dispatch("get_user_data", user.uid);

        return user;
      } catch (error) {
        let error_message = "An error occurred during login";

        switch (error.code) {
          case "auth/invalid-email":
            error_message = "Invalid email address";
            break;
          case "auth/user-disabled":
            error_message = "This account has been disabled";
            break;
          case "auth/user-not-found":
            error_message = "User not found";
            break;
          case "auth/wrong-password":
            error_message = "Invalid password";
            break;
          default:
            error_message = error.message;
        }

        commit("set_login_error", error_message);
        throw error;
      } finally {
        commit("set_loading", false);
      }
    },

    async logout({ commit }) {
      try {
        await signOut(auth);
        // Reset all auth related state
        commit("set_user", null);
        commit("set_jwt_token", null);
        commit("set_hasura_params", null);

        commit("set_user_data", null);

        console.clear();
        localStorage.clear();
        router.push("/login");
      } catch (error) {
        console.error("Logout error:", error);
        throw error;
      }
    },

    async send_password_reset({ commit }, email) {
      try {
        await sendPasswordResetEmail(auth, email);
      } catch (error) {
        commit("set_login_error", error.message);
        throw error;
      }
    },

    async confirm_password_reset({ commit }, { code, new_password }) {
      try {
        await confirmPasswordReset(auth, code, new_password);
      } catch (error) {
        commit("set_login_error", error.message);
        throw error;
      }
    },

    async update_password({ commit }, pass_params) {
      try {
        // Get current user
        const user = auth.currentUser;
        if (!user) {
          throw new Error("No user is currently signed in");
        }

        // Get user's email
        const email = user.email;

        // Create credential with current password
        const credential = EmailAuthProvider.credential(
          email,
          pass_params.current_password
        );

        // Re-authenticate user
        await reauthenticateWithCredential(user, credential);

        // Update password
        await updatePassword(user, pass_params.new_password);
        commit("set_loading", false);

        return true;

      } catch (error) {
        console.log(error);
      }
    },

    // This action can be expanded to fetch additional user data from your backend
    async get_user_data({ commit, state }, user_id) {
      try {
        let customer = (
          await axios.post(
            state.hasura_params.url,
            {
              query: queries.customer_by_pk,
              variables: {
                id: user_id,
              },
            },
            {
              headers: state.hasura_params.headers,
            }
          )
        );
        console.log("Customer data fetched:", customer.data);
        commit("set_user_data", customer.data.data.customer_by_pk);
        commit("set_customer_id", customer.data.data.customer_by_pk.id)

      } catch (error) {
        commit("set_user_data", null);
        console.error("Error fetching user data:", error);
        throw error;
      }
    },

    async init_auth({ commit, dispatch }) {
      return new Promise((resolve) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            const token = await user.getIdToken(true);
            commit("set_auth", auth);

            commit("set_user", user);
            commit("set_jwt_token", token);
            commit("set_hasura_params", token);
            await dispatch("get_user_data", user.uid);
          } else {
            commit("set_auth", null);

            commit("set_user", null);
            commit("set_jwt_token", null);
            commit("set_hasura_params", null);
            commit("set_user_data", null);
          }
          resolve();
        });
      });
    },
  },

  getters: {
    is_authenticated: (state) => !!state.user,
    current_user: (state) => state.user,
    jwt_token: (state) => state.jwt_token,
    login_error: (state) => state.login_error,
    user_data: (state) => state.user_ddb,
    hasura_params: (state) => state.hasura_params,
    auth: (state) => state.auth
  },
};
